/**
 * Data-bound blocks built by the setup dialog: line items tables, totals boxes, repeating
 * sections and conditional sections. generateBinding turns a setup into CDM, and readBinding turns
 * CDM of the same shape back into the setup that produced it — which is how "Edit binding" reopens
 * the dialog without any extra metadata stored in the document.
 *
 * A repeating table row is a loop around a one-row table, under a separate header table; widths
 * are shared out over the page content width the catalog's tables use.
 */
import { blocksToPm } from '../cdm/fromCdm'
import { formatterDef, formatterText } from '../cdm/formatters'
import {
  HAlign, type ConditionalBlock, type DocBlock, type DocInline, type FormatterSpec, type LoopBlock, type ParagraphBlock,
  type TableBlock, type TableCell,
} from '../cdm/types'
import { CS, Each, FieldCell, FP, HeadCell, If, P, PA, Row, T, Table, withFormat } from './cdmBuilders'

export type BlockSetup = 'lineItems' | 'totals' | 'repeating' | 'conditional'
export type Align = 'left' | 'right'

export const CONTENT_WIDTH_PT = 468
export const TOTALS_WIDTHS = [378, 90]
export const LOOP_ALIAS = 'item'

export interface BoundField {
  /** Relative to the loop item for line items and repeating sections; a full path for totals. */
  field: string
  label: string
  formatters: FormatterSpec[] | null
}
export interface LineItemColumn extends BoundField { align: Align; width: number }
export interface TotalsRow extends BoundField { total: boolean }

export interface LineItemsSetup { kind: 'lineItems'; list: string; columns: LineItemColumn[] }
export interface RepeatingSetup { kind: 'repeating'; list: string; fields: BoundField[]; layout: 'paragraphs' | 'line' }
export interface TotalsSetup { kind: 'totals'; rows: TotalsRow[] }
export interface ConditionalSetup { kind: 'conditional'; field: string; negate: boolean; withElse: boolean }
export type BindingSetup = LineItemsSetup | RepeatingSetup | TotalsSetup | ConditionalSetup

export const LINE_JOIN = ' — '

/** unitPrice → "Unit price", ship_to.postal-code → "Postal code"; all-caps words (SKU) are kept. */
export function humanizeKey(key: string): string {
  const last = key.split('.').filter(Boolean).pop() ?? ''
  const words = last.split(/(?<=[a-z0-9])(?=[A-Z])|[_\-\s]+/).filter(Boolean)
    .map(w => (w.length > 1 && w === w.toUpperCase() ? w : w.toLowerCase()))
  const text = words.join(' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** Positive weights scaled to whole points summing to `total` (largest remainder). Exact inputs come back unchanged. */
export function fitWidths(weights: number[], total = CONTENT_WIDTH_PT): number[] {
  if (!weights.length) return []
  const w = weights.map(x => (Number.isFinite(x) && x > 0 ? x : 1))
  const sum = w.reduce((a, b) => a + b, 0)
  const raw = w.map(x => (x / sum) * total)
  const out = raw.map(Math.floor)
  let left = total - out.reduce((a, b) => a + b, 0)
  const order = raw.map((x, i) => ({ i, frac: x - Math.floor(x) })).sort((a, b) => b.frac - a.frac || a.i - b.i)
  for (let k = 0; left > 0; k = (k + 1) % order.length, left--) out[order[k].i]++
  return out
}

/** Weights with column `i` set to `pct` percent of the total and the others keeping their proportions. */
export function setShare(weights: number[], i: number, pct: number): number[] {
  if (weights.length < 2) return fitWidths(weights)
  const p = Math.min(95, Math.max(5, pct))
  const others = weights.reduce((a, b, j) => (j === i ? a : a + (b > 0 ? b : 1)), 0)
  return fitWidths(weights.map((w, j) => (j === i ? (p / (100 - p)) * others : w)))
}

const MONEY_KEY = /price|amount|total|cost|subtotal|tax|fee|balance|discount|shipping/i
const RIGHT_ALIGNED = new Set(['currency', 'number', 'round', 'floor', 'ceil', 'count', 'sum'])

export const currency = (code = 'USD'): FormatterSpec[] => [{ name: 'currency', args: [code] }]

/** A sample value's default formatter: currency for money-like numbers, otherwise none. */
export function defaultFormatters(key: string, value?: unknown): FormatterSpec[] | null {
  return typeof value === 'number' && MONEY_KEY.test(key) ? currency() : null
}

export function defaultAlign(value: unknown, formatters: FormatterSpec[] | null): Align {
  return typeof value === 'number' || (formatters ?? []).some(f => RIGHT_ALIGNED.has(f.name.toLowerCase())) ? 'right' : 'left'
}

/** The formatter choices the dialog offers for a column (Number and Date groups of formatters.ts). */
export interface FormatChoice { label: string; formatters: FormatterSpec[] | null }

export const FORMAT_CHOICES: FormatChoice[] = [
  { label: 'None', formatters: null },
  ...(formatterDef('currency')?.arg?.choices ?? []).map(c => ({ label: `Currency (${c})`, formatters: currency(c) })),
  { label: 'Number (N0)', formatters: [{ name: 'number', args: ['N0'] }] },
  { label: 'Number (N2)', formatters: [{ name: 'number', args: ['N2'] }] },
  { label: 'Date (MMMM d, yyyy)', formatters: [{ name: 'date', args: [formatterDef('date')?.arg?.defaultValue ?? 'MMMM d, yyyy'] }] },
  { label: 'Date (short)', formatters: [{ name: 'dateshort', args: [] }] },
  { label: 'Date (long)', formatters: [{ name: 'datelong', args: [] }] },
  { label: 'Date and time', formatters: [{ name: 'datetime', args: [] }] },
]

/** A stable key for a formatter pipeline, as the select's option value. */
export const formatKey = (formatters: FormatterSpec[] | null): string => (formatters ?? []).map(formatterText).join(' | ')

// ============ generate ============

const align = (a: Align) => (a === 'right' ? HAlign.Right : HAlign.Left)
const itemPath = (field: string) => `${LOOP_ALIAS}.${field}`

export function generateBinding(setup: BindingSetup): DocBlock[] {
  switch (setup.kind) {
    case 'lineItems': {
      const widths = fitWidths(setup.columns.map(c => c.width))
      return [
        Table(widths, false, [Row(setup.columns.map(c => HeadCell(c.label, align(c.align))), true)]),
        Each(setup.list, LOOP_ALIAS, [Table(widths, false, [Row(setup.columns.map(c => FieldCell(FP(itemPath(c.field), c.formatters), align(c.align))))])]),
      ]
    }
    case 'repeating': {
      const ph = (f: BoundField) => FP(itemPath(f.field), f.formatters)
      const prefix = (f: BoundField) => (f.label ? `${f.label}: ` : '')
      if (setup.layout === 'paragraphs') {
        return [Each(setup.list, LOOP_ALIAS, setup.fields.map(f => P([...(prefix(f) ? [T(prefix(f))] : []), ph(f)])))]
      }
      const inlines: DocInline[] = setup.fields.flatMap((f, i) => {
        const text = (i ? LINE_JOIN : '') + prefix(f)
        return text ? [T(text), ph(f)] : [ph(f)]
      })
      return [Each(setup.list, LOOP_ALIAS, [P(inlines)])]
    }
    case 'totals':
      return [Table(TOTALS_WIDTHS, false, setup.rows.map(r => Row([
        CS(null, [PA(HAlign.Right, [T(r.label, r.total ? { bold: true } : {})])]),
        FieldCell(r.total ? withFormat(FP(r.field, r.formatters), { bold: true }) : FP(r.field, r.formatters), HAlign.Right),
      ])))]
    case 'conditional':
      return [{ ...If(setup.field, [P()]), negate: setup.negate, elseBody: setup.withElse ? [P()] : null }]
  }
}

export const bindingJson = (setup: BindingSetup) => blocksToPm(generateBinding(setup))

// ============ read ============

const isParagraph = (b: DocBlock | undefined): b is ParagraphBlock => b?.$type === 'paragraph'

function cellParagraph(cell: TableCell): ParagraphBlock | null {
  return cell.content.length === 1 && isParagraph(cell.content[0]) ? cell.content[0] : null
}

/** The cell's text when it is one paragraph of plain text runs, else null. */
function cellText(cell: TableCell): { text: string; bold: boolean } | null {
  const p = cellParagraph(cell)
  if (!p || !p.inlines.every(i => i.$type === 'run')) return null
  const runs = p.inlines as Extract<DocInline, { $type: 'run' }>[]
  return { text: runs.map(r => r.text).join(''), bold: runs.length > 0 && runs.every(r => r.format.bold) }
}

/** The cell's lone placeholder and its paragraph's alignment, else null. */
function cellPlaceholder(cell: TableCell) {
  const p = cellParagraph(cell)
  const ph = p?.inlines.length === 1 ? p.inlines[0] : null
  if (!p || ph?.$type !== 'placeholder') return null
  return { ph, align: (p.format.alignment === HAlign.Right ? 'right' : 'left') as Align }
}

const clone = (f: FormatterSpec[] | null) => (f?.length ? f.map(x => ({ name: x.name, args: [...x.args] })) : null)

function itemField(loop: LoopBlock, rawName: string): string | null {
  const prefix = `${loop.itemAlias}.`
  return rawName.startsWith(prefix) && rawName.length > prefix.length ? rawName.slice(prefix.length) : null
}

function readHeader(table: TableBlock, count: number): string[] | null {
  if (table.rows.length !== 1 || table.rows[0].cells.length !== count) return null
  const texts = table.rows[0].cells.map(cellText)
  return texts.every(t => t !== null) ? texts.map(t => t!.text) : null
}

function readLineItems(header: TableBlock | null, loop: LoopBlock): LineItemsSetup | null {
  const inner = loop.body.length === 1 && loop.body[0].$type === 'table' ? loop.body[0] : null
  if (!inner || inner.rows.length !== 1) return null
  const cells = inner.rows[0].cells
  if (!cells.length) return null
  // A header that does not line up is not ours: the caller retries without it rather than replace it.
  const labels = header ? readHeader(header, cells.length) : null
  if (header && !labels) return null
  const widths = inner.columnWidthsPt?.length === cells.length ? inner.columnWidthsPt : fitWidths(cells.map(() => 1))
  const columns: LineItemColumn[] = []
  for (let i = 0; i < cells.length; i++) {
    const c = cellPlaceholder(cells[i])
    const field = c && itemField(loop, c.ph.rawName)
    if (!c || !field) return null
    columns.push({ field, label: labels?.[i] ?? humanizeKey(field), formatters: clone(c.ph.formatters), align: c.align, width: widths[i] })
  }
  return { kind: 'lineItems', list: loop.rawName, columns }
}

/** "Label: " → "Label", "" → "", anything else is not a generated prefix. */
function readPrefix(text: string): string | null {
  if (text === '') return ''
  return text.endsWith(': ') && text.length > 2 ? text.slice(0, -2) : null
}

function readRepeating(loop: LoopBlock): RepeatingSetup | null {
  if (!loop.body.length || !loop.body.every(isParagraph)) return null
  const paras = loop.body as ParagraphBlock[]
  const parse = (inlines: DocInline[], joined: boolean): BoundField[] | null => {
    const out: BoundField[] = []
    let text = ''
    for (const inl of inlines) {
      if (inl.$type === 'run') { text += inl.text; continue }
      if (inl.$type !== 'placeholder') return null
      const field = itemField(loop, inl.rawName)
      if (!field) return null
      if (joined && out.length) {
        if (!text.startsWith(LINE_JOIN)) return null
        text = text.slice(LINE_JOIN.length)
      }
      const label = readPrefix(text)
      if (label === null) return null
      out.push({ field, label, formatters: clone(inl.formatters) })
      text = ''
    }
    return text === '' && out.length ? out : null
  }
  const placeholders = (p: ParagraphBlock) => p.inlines.filter(i => i.$type === 'placeholder').length
  if (paras.length === 1 && placeholders(paras[0]) > 1) {
    const fields = parse(paras[0].inlines, true)
    return fields && { kind: 'repeating', list: loop.rawName, fields, layout: 'line' }
  }
  const fields: BoundField[] = []
  for (const p of paras) {
    const f = placeholders(p) === 1 ? parse(p.inlines, false) : null
    if (!f) return null
    fields.push(f[0])
  }
  return { kind: 'repeating', list: loop.rawName, fields, layout: 'paragraphs' }
}

function readTotals(table: TableBlock): TotalsSetup | null {
  if (!table.rows.length) return null
  const rows: TotalsRow[] = []
  for (const row of table.rows) {
    if (row.cells.length !== 2) return null
    const label = cellText(row.cells[0])
    const value = cellPlaceholder(row.cells[1])
    if (!label || !value) return null
    rows.push({ field: value.ph.rawName, label: label.text, formatters: clone(value.ph.formatters), total: label.bold })
  }
  return { kind: 'totals', rows }
}

function readConditional(c: ConditionalBlock): ConditionalSetup {
  return { kind: 'conditional', field: c.rawName, negate: c.negate, withElse: c.elseBody !== null }
}

/**
 * The setup that would generate `blocks`, or null when they are not a generated shape:
 * [header table, loop] or [loop] → line items (else repeating), [table] → totals, [conditional].
 */
export function readBinding(blocks: DocBlock[]): BindingSetup | null {
  const [a, b] = blocks
  if (blocks.length === 2 && a.$type === 'table' && b.$type === 'loop') return readLineItems(a, b)
  if (blocks.length !== 1) return null
  if (a.$type === 'loop') return readLineItems(null, a) ?? readRepeating(a)
  if (a.$type === 'table') return readTotals(a)
  if (a.$type === 'conditional') return readConditional(a)
  return null
}
